import type {
  UpsertWellnessFocus,
  WellnessFocus,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-212 — Wellness Plan authoring client, mirroring journey-phases-api.ts.
// Auth rides the httpOnly clinician session cookie, scoped by tenantHeader().

function commonHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...tenantHeader() };
}

export class WellnessApiError extends Error {}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    return jsonOr401<T>(res);
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
      else if (Array.isArray(msg) && msg.length) detail = msg.join("; ");
    } catch {
      // non-JSON body — keep the status-code detail
    }
    throw new WellnessApiError(detail);
  }
  return (await res.json()) as T;
}

const BASE = `${API_BASE}/employee-api/v1/wellness`;

export async function listWellness(): Promise<WellnessFocus[]> {
  const res = await apiFetch(BASE, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function fetchWellnessFocus(
  dimension: string,
): Promise<WellnessFocus> {
  const res = await apiFetch(`${BASE}/${dimension}`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function createWellnessFocus(
  body: UpsertWellnessFocus,
): Promise<WellnessFocus> {
  const res = await apiFetch(BASE, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function updateWellnessFocus(
  dimension: string,
  body: UpsertWellnessFocus,
): Promise<WellnessFocus> {
  const res = await apiFetch(`${BASE}/${dimension}`, {
    method: "PUT",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function publishWellnessFocus(
  dimension: string,
): Promise<WellnessFocus> {
  const res = await apiFetch(`${BASE}/${dimension}/publish`, {
    method: "POST",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function archiveWellnessFocus(
  dimension: string,
): Promise<WellnessFocus> {
  const res = await apiFetch(`${BASE}/${dimension}/archive`, {
    method: "POST",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}
